import React, { useCallback, useContext, useRef, useState } from 'react';
import classNames from 'classnames';

import { Toast, ToastProps } from '../components';

export type ToastOptions = Omit<
  ToastProps,
  'message' | 'className' | 'onClose'
> & {
  text?: React.ReactNode;
  id?: string;
};

export type ToastInstace = ToastOptions & { id: string };

type ToastContextType = {
  addToast: (options: ToastOptions) => string;
  removeToast: (id: string) => void;
  setHasBottomMargin: (value: boolean) => void;
};

const ToastContext = React.createContext<ToastContextType>(
  {} as ToastContextType
);

const ManagedToast = ({
  id,
  text,
  onClose,
  ...rest
}: ToastInstace & { onClose: (id: string) => void }) => {
  const handleClose = useCallback(() => {
    onClose(id);
  }, [onClose, id]);

  return (
    <Toast
      {...rest}
      message={text}
      className="flex-1 pointer-events-auto"
      onClose={handleClose}
    />
  );
};

type ToastProviderProps = {
  placement?: 'bottom' | 'top';
  isFullWidth?: boolean;
  className?: string;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  placement = 'bottom',
  isFullWidth = false,
  className,
}) => {
  const [toasts, setToasts] = useState<ToastInstace[]>([]);
  const autoIdRef = useRef(0);
  const [hasBottomMargin, setHasBottomMargin] = useState(false);

  const removeToast = useCallback((id: string) => {
    setToasts((prevState) => prevState.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((options: ToastOptions) => {
    const { id = `${(autoIdRef.current += 1)}`, ...rest } = options;

    setToasts((prevState) => [
      ...prevState,
      {
        id,
        ...rest,
      },
    ]);

    return id;
  }, []);

  return (
    <ToastContext.Provider
      value={{
        addToast,
        removeToast,
        setHasBottomMargin,
      }}
    >
      <>
        <div
          className={classNames(
            'z-50 left-0 right-0',
            'mx-auto space-y-4 flex flex-col pointer-events-none',
            {
              'max-w-3xl': !isFullWidth,
              'absolute top-0 mt-4': placement === 'top',
              'fixed mb-4 bottom-0': placement === 'bottom',
              'bottom-18': placement === 'bottom' && hasBottomMargin,
            },
            className
          )}
        >
          {toasts.map((toast) => (
            <ManagedToast key={toast.id} {...toast} onClose={removeToast} />
          ))}
        </div>
        {children}
      </>
    </ToastContext.Provider>
  );
};

export const useToastNotifications = () => useContext(ToastContext);
